import * as React from "react";
import { FileText, Search, X, Loader, CornerDownLeft, CornerUpRight, Eye } from "lucide-react";
import { useTemplates } from "@/hooks/useTemplates";
import type { TextTemplate } from "@/types/knowledgeBank.types";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { InputGroup, InputGroupAddon, InputGroupInput } from "./ui/input-group";

interface KnowledgeBankDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsertTextarea: (text: string) => void;
  onInsertDocument: (text: string) => void;
}

const TemplateItem: React.FC<{
  template: TextTemplate;
  onInsertTextarea: () => void;
  onInsertDocument: () => void;
  onView: () => void;
}> = ({ template, onInsertTextarea, onInsertDocument, onView }) => (
  <div className="flex items-start justify-between px-3 py-2 bg-primary-100 rounded-lg transition-all">
    <div className="flex items-start gap-2 flex-1 min-w-0">
      <FileText className="size-3.5 text-neutral-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-neutral-400 line-clamp-1">
          {template.template_name}
        </div>
        {template.text.trim() !== "" && (
          <div className="text-xs text-neutral-500 line-clamp-2">{template.text}</div>
        )}
      </div>
    </div>
    <TemplateActions
      onInsertDocument={onInsertDocument}
      onInsertTextarea={onInsertTextarea}
      onView={onView}
    />
  </div>
);

const TemplateActions: React.FC<{
  onInsertDocument: () => void;
  onInsertTextarea: () => void;
  onView: () => void;
}> = ({ onInsertDocument, onInsertTextarea, onView }) => (
  <div className="flex flex-col items-center gap-1.5 ml-2 shrink-0">
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" className="w-auto h-auto p-0" size="icon" onClick={onView}>
          <Eye className="size-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">View full content</TooltipContent>
    </Tooltip>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          className="w-auto h-auto p-0"
          size="icon"
          onClick={onInsertDocument}
        >
          <CornerUpRight className="size-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">Insert into document</TooltipContent>
    </Tooltip>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          className="w-auto h-auto p-0"
          size="icon"
          onClick={onInsertTextarea}
        >
          <CornerDownLeft className="size-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">Insert into consideration</TooltipContent>
    </Tooltip>
  </div>
);

const TemplatePreviewDialog: React.FC<{
  template: TextTemplate | null;
  onClose: () => void;
}> = ({ template, onClose }) => (
  <Dialog
    open={template != null}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
  >
    <DialogContent className="max-w-[calc(100vw-2rem)] max-h-[80vh] flex flex-col">
      <DialogHeader>
        <DialogTitle className="text-sm font-semibold">{template?.template_name}</DialogTitle>
      </DialogHeader>
      <ScrollArea className="flex-1">
        <p className="text-xs text-neutral-400 whitespace-pre-wrap">{template?.text}</p>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

const TemplateList: React.FC<{
  templates: TextTemplate[];
  onInsertTextarea: (text: string) => void;
  onInsertDocument: (text: string) => void;
  onView: (template: TextTemplate) => void;
}> = ({ templates, onInsertTextarea, onInsertDocument, onView }) => {
  if (templates.length === 0) {
    return <p className="text-sm text-neutral-500 text-center py-8">No templates found</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      {templates.map((template) => (
        <TemplateItem
          key={template.id}
          template={template}
          onInsertTextarea={() => onInsertTextarea(template.text)}
          onInsertDocument={() => onInsertDocument(template.text)}
          onView={() => onView(template)}
        />
      ))}
    </div>
  );
};

const SearchBar: React.FC<{
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}> = ({ searchQuery, setSearchQuery }) => (
  <div className="p-2 border-b shrink-0">
    <InputGroup>
      <InputGroupAddon>
        <Search className="size-3.5 text-neutral-500" />
      </InputGroupAddon>
      <InputGroupInput
        placeholder="Search templates..."
        value={searchQuery}
        className="placeholder:text-neutral-500"
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      {searchQuery !== "" && (
        <InputGroupAddon align="inline-end">
          <X
            className="size-3.5 text-neutral-500 cursor-pointer hover:text-neutral-300"
            onClick={() => setSearchQuery("")}
          />
        </InputGroupAddon>
      )}
    </InputGroup>
  </div>
);

function useFilteredTemplates(templates: TextTemplate[] | undefined, searchQuery: string) {
  return React.useMemo(() => {
    if (!templates) return [];
    if (searchQuery.trim() === "") return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(
      (t) => t.template_name.toLowerCase().includes(query) || t.text.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);
}

const KnowledgeBankDialog: React.FC<KnowledgeBankDialogProps> = ({
  open,
  onOpenChange,
  onInsertTextarea,
  onInsertDocument,
}) => {
  const { data: templates, isLoading } = useTemplates();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [previewTemplate, setPreviewTemplate] = React.useState<TextTemplate | null>(null);
  const filteredTemplates = useFilteredTemplates(templates, searchQuery);

  const createHandler = (cb: (text: string) => void) => (text: string) => {
    cb(text);
    onOpenChange(false);
    setSearchQuery("");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="p-0 gap-0 max-w-[calc(100vw-2rem)] h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader className="p-3 border-b shrink-0">
            <DialogTitle className="text-sm font-semibold">Knowledge Bank</DialogTitle>
          </DialogHeader>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          <ScrollArea className="flex-1 min-h-0 p-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="size-5 animate-spin text-neutral-400" />
              </div>
            ) : (
              <TemplateList
                templates={filteredTemplates}
                onInsertTextarea={createHandler(onInsertTextarea)}
                onInsertDocument={createHandler(onInsertDocument)}
                onView={setPreviewTemplate}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
      <TemplatePreviewDialog template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
    </>
  );
};

export default KnowledgeBankDialog;
